import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FieldSettlementRecord, RoutePlanRecord, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-distribution-settlements',
  imports: [AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, SelectModule],
  template: `
    <ain-page [title]="t('sales.distribution.settlements.title')" [description]="t('sales.distribution.settlements.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <label>
            <span>{{ t('sales.distribution.settlements.fields.route') }}</span>
            <p-select [options]="routeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="routeFilter" [showClear]="true" appendTo="body" (ngModelChange)="applyFilter()">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? item.label : t('common.all') }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ item.label }}</ng-template>
            </p-select>
          </label>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('sales.distribution.settlements.fields.settlementNumber') }}</th>
                <th>{{ t('sales.distribution.settlements.fields.route') }}</th>
                <th>{{ t('sales.distribution.settlements.fields.amount') }}</th>
                <th>{{ t('sales.distribution.settlements.fields.settledAt') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><button type="button" class="link-btn" (click)="openRoute(row.routePlanId)">{{ row.settlementNumber }}</button></td>
                <td>{{ routeLabel(row.routePlanId) }}</td>
                <td>{{ row.cashCollectedAmount | ainAmount }} {{ row.currencyCode }}</td>
                <td>{{ row.settledAtUtc | ainDate }}</td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`.panel{display:grid;gap:10px}.toolbar{display:flex;gap:8px}label{display:grid;gap:6px;max-width:320px}.link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}`]
})
export class SalesDistributionSettlementsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly sourceRows = signal<FieldSettlementRecord[]>([]);
  readonly rows = signal<FieldSettlementRecord[]>([]);
  readonly routes = signal<RoutePlanRecord[]>([]);
  routeFilter: string | null = null;

  ngOnInit(): void {
    this.api.listDistributionRoutes().subscribe({
      next: routes => this.routes.set(routes),
      error: () => this.routes.set([])
    });
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.listFieldSettlements().subscribe({
      next: items => { this.sourceRows.set(items); this.applyFilter(); this.loading.set(false); },
      error: () => { this.sourceRows.set([]); this.rows.set([]); this.loading.set(false); }
    });
  }

  routeOptions(): { label: string; value: string }[] {
    return this.routes().map(r => ({ label: r.routeNumber, value: r.id }));
  }

  routeLabel(routePlanId: string): string {
    return this.routes().find(r => r.id === routePlanId)?.routeNumber ?? routePlanId;
  }

  applyFilter(): void {
    const filter = this.routeFilter;
    this.rows.set(filter ? this.sourceRows().filter(r => r.routePlanId === filter) : this.sourceRows());
  }

  openRoute(routePlanId: string): void {
    void this.router.navigate(['/sales/distribution-routes', routePlanId]);
  }

  t(key: string): string { return this.localization.translate(key); }
}
